/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { LegacyDataTable, LegacyDataTableBody, LegacyDataTableHeader, LegacyDataTableHeaderColumn, LegacyDataTableRow, LegacyDataTableRowColumn } from "@octopusdeploy/design-system-components";
import type { EnvironmentV2Resource, TenantResource, ProjectGroupResource, UserPermissionRestriction, PermissionDescriptions, PermissionDescription, ProjectSummaryResource } from "@octopusdeploy/octopus-server-client";
import { isAllProjectGroups, isAllProjects, isAllEnvironments, isAllTenants } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { sortBy } from "lodash";
import * as React from "react";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
interface Props {
    permissions: Record<string, UserPermissionRestriction[]>;
    permissionDescriptions: PermissionDescriptions;
    projectSummaries: Record<string, ProjectSummaryResource>;
    environments: Record<string, EnvironmentV2Resource>;
    tenants: Record<string, TenantResource>;
    projectGroups: Record<string, ProjectGroupResource>;
    showRestrictions: boolean;
}
interface PermissionDetails {
    name: string;
    description: PermissionDescription;
    restrictions: UserPermissionRestriction[];
}
type restrictionType = "projects" | "projectgroups" | "tenants" | "environments";
const PermissionTable = (props: Props) => {
    const supportsRestriction = (p: PermissionDetails, category: restrictionType) => p.restrictions && p.description.SupportedRestrictions.indexOf(category) >= 0;
    const buildLink = (collection: Record<string, {
        Name: string;
        SpaceId?: string;
    }>, id: string, getlink: (spaceId: string) => LinkHref) => {
        if (!collection.hasOwnProperty(id)) {
            return id;
        }
        const name = collection[id].Name;
        const spaceId = collection[id].SpaceId;
        const path = getlink(spaceId!);
        return (<InternalLink key={`${path}-${name}`} to={path}>
                {name}
            </InternalLink>);
    };
    const commaDelimitList = (list: React.ReactNode[]) => {
        const result: React.ReactNode[] = [];
        list.forEach((l, i) => {
            result.push(l);
            if (i !== list.length - 1) {
                result.push(", ");
            }
        });
        return result;
    };
    const permissionsDetails: PermissionDetails[] = Object.keys(props.permissionDescriptions)
        .filter((p) => props.permissions[p])
        .map((p) => ({ name: p, description: props.permissionDescriptions[p], restrictions: props.permissions[p] }));
    const sortedPermissionDetails: PermissionDetails[] = sortBy(permissionsDetails, "name");
    return (<React.Fragment>
            {sortedPermissionDetails.length > 0 && (<LegacyDataTable>
                    <LegacyDataTableHeader>
                        <LegacyDataTableRow>
                            <LegacyDataTableHeaderColumn>Permission</LegacyDataTableHeaderColumn>
                            {props.showRestrictions && <LegacyDataTableHeaderColumn>Project Groups</LegacyDataTableHeaderColumn>}
                            {props.showRestrictions && <LegacyDataTableHeaderColumn>Projects</LegacyDataTableHeaderColumn>}
                            {props.showRestrictions && <LegacyDataTableHeaderColumn>Environments</LegacyDataTableHeaderColumn>}
                            {props.showRestrictions && <LegacyDataTableHeaderColumn>Tenants</LegacyDataTableHeaderColumn>}
                        </LegacyDataTableRow>
                    </LegacyDataTableHeader>
                    <LegacyDataTableBody>
                        {sortedPermissionDetails.map((permissionDetail) => permissionDetail.restrictions.map((restriction, index) => (<LegacyDataTableRow key={permissionDetail.name + index}>
                                    {index === 0 ? <LegacyDataTableRowColumn title={permissionDetail.description.Description}>{permissionDetail.name}</LegacyDataTableRowColumn> : <LegacyDataTableRowColumn />}

                                    {props.showRestrictions && (<LegacyDataTableRowColumn>
                                            {supportsRestriction(permissionDetail, "projects") && (<span>
                                                    {!isAllProjectGroups(restriction.RestrictedToProjectGroupIds)
                            ? commaDelimitList(restriction.RestrictedToProjectGroupIds.map((id: string) => buildLink(props.projectGroups, id, (spaceId: string) => links.projectGroupPage.generateUrl({ spaceId, projectGroupId: id }))))
                            : "All project groups"}
                                                </span>)}
                                        </LegacyDataTableRowColumn>)}
                                    {props.showRestrictions && (<LegacyDataTableRowColumn>
                                            {supportsRestriction(permissionDetail, "projects") && (<span>
                                                    {!isAllProjects(restriction.RestrictedToProjectIds)
                            ? commaDelimitList(restriction.RestrictedToProjectIds.map((id: string) => buildLink(props.projectSummaries, id, (spaceId: string) => links.projectRootRedirect.generateUrl({ spaceId, projectSlug: id }))))
                            : "All projects"}
                                                </span>)}
                                        </LegacyDataTableRowColumn>)}
                                    {props.showRestrictions && (<LegacyDataTableRowColumn>
                                            {supportsRestriction(permissionDetail, "environments") && (<span>
                                                    {!isAllEnvironments(restriction.RestrictedToEnvironmentIds)
                            ? commaDelimitList(restriction.RestrictedToEnvironmentIds.map((id: string) => {
                                const environment = props.environments[id];
                                if (environment && environment.Type === "Parent") {
                                    return buildLink(props.environments, id, (spaceId: string) => links.parentEnvironmentPage.generateUrl({ spaceId, environmentId: id }));
                                }
                                return buildLink(props.environments, id, (spaceId: string) => links.environmentPage.generateUrl({ spaceId, environmentId: id }));
                            }))
                            : "All environments"}
                                                </span>)}
                                        </LegacyDataTableRowColumn>)}
                                    {props.showRestrictions && (<LegacyDataTableRowColumn>
                                            {supportsRestriction(permissionDetail, "tenants") && (<span>
                                                    {!isAllTenants(restriction.RestrictedToTenantIds)
                            ? commaDelimitList(restriction.RestrictedToTenantIds.map((id: string) => buildLink(props.tenants, id, (spaceId: string) => links.tenantOverviewPage.generateUrl({ spaceId, tenantId: id }))))
                            : "All tenants"}
                                                </span>)}
                                        </LegacyDataTableRowColumn>)}
                                </LegacyDataTableRow>)))}
                    </LegacyDataTableBody>
                </LegacyDataTable>)}
        </React.Fragment>);
};
export { PermissionTable };
